'use client';

import SideDrawer from 'src/components/Drawers/BaseDrawers/SideDrawer/SideDrawer';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { DrawerName, useDrawersStore } from 'src/stores/useDrawersStore';
import {
	BigNum,
	BN,
	calculateAssetWeight,
	calculateLiabilityWeight,
	getWorstCaseTokenAmounts,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	SPOT_MARKET_WEIGHT_PRECISION,
	SpotBalanceType,
	StrictOraclePrice,
	ZERO,
} from '@velocity-exchange/sdk';
import { memo, useMemo } from 'react';
import useSafePush from 'src/hooks/useSafePush';
import { ArrowRight } from '@velocity-exchange/icons';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useVelocityClient from 'src/hooks/useVelocityClient';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { VELOCITY_DOCS_URL } from 'src/constants/constants';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { cm } from 'src/utils/classNames';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { ZERO_BIGNUM } from 'src/constants/math';
import { ENUM_UTILS, UIMarket } from '@velocity-exchange/common';
import { RateHistoryChart } from 'src/components/RateHistoryChart';
import BorrowLendRateToUtilizationChart from '../../BorrowLendRateToUtilizationChart';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { formatMarketSymbolByContext } from 'src/utils/marketSymbol';
import ApyBreakdownTooltipV2 from 'src/components/Tooltip/ApyBreakdownTooltipV2';
import { useApyBreakdownTooltipData } from 'src/hooks/useApyBreakdownTooltipData';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import useModal from 'src/components/Modals/hooks/useModal';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import { TabOption } from 'src/components/primitives/Tabs/types';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import Button from 'src/components/primitives/Button/Button';
import Divider from 'src/components/primitives/Divider/Divider';
import { InitialAssetWeightGraph } from 'src/components/Charts/InitialAssetWeightGraph/InitialAssetWeightGraph';
import Link from 'src/components/primitives/Link/Link';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { PRESETS, legacyTrim } from 'src/utils/formatNumber/presets';
import { formatText } from '@velocity-exchange/common/format';

type BorrowLendTabValue = 'deposit' | 'borrow';

const ONE_HUNDRED = BigNum.from(100);

// Local wrapper components
const DrawerContainer = ({
	children,
	className,
	drawerName,
}: {
	children: React.ReactNode;
	className?: string;
	drawerName: DrawerName;
}) => {
	return (
		<SideDrawer.Container drawerName={drawerName} className={className}>
			{children}
		</SideDrawer.Container>
	);
};

const DrawerTitle = ({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	return <SideDrawer.Title className={className}>{children}</SideDrawer.Title>;
};

const DrawerBody = ({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	return <SideDrawer.Body className={className}>{children}</SideDrawer.Body>;
};

const DrawerFooter = ({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	return (
		<SideDrawer.Footer className={className}>{children}</SideDrawer.Footer>
	);
};

const TabOptions: TabOption<BorrowLendTabValue>[] = [
	{ value: 'deposit', label: 'Deposit (Lend)' },
	{ value: 'borrow', label: 'Borrow' },
];

const Section = ({
	sectionLabel,
	topRightNode,
	children,
}: {
	sectionLabel: string;
	topRightNode?: React.ReactNode;
	children: React.ReactNode;
}) => {
	return (
		<div className="flex flex-col gap-4">
			<div className="flex w-full items-center justify-between">
				<span className="typo-label-sm text-text-default">{sectionLabel}</span>
				{topRightNode && topRightNode}
			</div>
			<div className="flex flex-col gap-2">{children}</div>
		</div>
	);
};

const DetailRow = ({
	label,
	value,
	marketSymbol,
	className,
}: {
	label: React.ReactNode;
	value: React.ReactNode;
	marketSymbol?: string;
	className?: string;
}) => (
	<div className={cm('flex h-lg w-full justify-between', className)}>
		<span className="typo-body-sm text-text-secondary">{label}</span>
		<div className="flex items-center gap-1">
			{marketSymbol && (
				<MarketIcon marketSymbol={marketSymbol} sizeClass="w-4 h-4" />
			)}
			<span className="typo-body-sm text-text-default">{value}</span>
		</div>
	</div>
);

const DepositMarketContent = ({
	market: spotMarketConfig,
	targetAccountKey,
}: {
	market: UIMarket;
	targetAccountKey?: string;
}) => {
	const { show } = useModal();
	const getOraclePrice = useGetOraclePriceForMarket();
	const safePush = useSafePush();
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const borrowLendData = useVelocityStore((s) =>
		s.borrowLendData.find(
			(data) => data.bankIndex === spotMarketConfig.marketIndex
		)
	);
	const targetUserClient = useVelocityAccountStore((s) =>
		targetAccountKey
			? s.accounts[targetAccountKey]?.client
			: s.getCurrentUserAccount()?.client
	);
	const spotBalance = useVelocityAccountStore(
		(s) =>
			s.accounts[
				targetAccountKey ? targetAccountKey : (s.currentUserKey ?? '')
			]?.spotBalances?.find(
				(balance) => balance.asset.marketIndex === spotMarketConfig.marketIndex
			) || null
	);

	const spotPosition = useMemo(() => {
		return targetUserClient
			?.getActiveSpotPositions()
			.find((pos) => pos.marketIndex === spotMarketConfig.marketIndex);
	}, [spotMarketConfig.marketIndex, !!targetUserClient]);

	const userDeposits = ENUM_UTILS.match(
		spotBalance?.balanceType,
		SpotBalanceType.DEPOSIT
	)
		? (spotBalance?.balance ?? ZERO_BIGNUM)
		: ZERO_BIGNUM;

	const liqPrice = BigNum.from(
		targetUserClient?.spotLiquidationPrice(spotMarketConfig.marketIndex),
		PRICE_PRECISION_EXP
	);

	const apyData = useApyBreakdownTooltipData(spotMarketConfig.marketIndex);

	const spotMarketAccount = velocityClientIsReady
		? velocityClient?.getSpotMarketAccount(spotMarketConfig.marketIndex)
		: undefined;
	if (!spotMarketAccount) {
		return null;
	}

	const maxDeposits = BigNum.from(
		spotMarketAccount?.maxTokenDeposits || new BN(0),
		spotMarketConfig?.precisionExp
	);

	const oraclePriceData = spotMarketAccount
		? velocityClient?.getOracleDataForSpotMarket(spotMarketAccount.marketIndex)
		: undefined;

	const oraclePrice = getOraclePrice(spotMarketConfig.marketId)?.val ?? ZERO;

	const strictOraclePrice = new StrictOraclePrice(
		oraclePriceData?.price ?? ZERO
	);
	const maxMarginRatio =
		targetUserClient?.getUserAccount()?.maxMarginRatio ?? 0;

	const {
		weight: worstCaseWeightInitial,
		weightedTokenValue: weightedTokenValueInitial,
	} =
		spotPosition && spotMarketAccount && targetUserClient
			? getWorstCaseTokenAmounts(
					spotPosition,
					spotMarketAccount,
					strictOraclePrice,
					'Initial',
					maxMarginRatio
				)
			: { weight: null, weightedTokenValue: null };
	const {
		weight: worstCaseWeightMaintenance,
		weightedTokenValue: weightedTokenValueMaintenance,
	} =
		spotPosition && spotMarketAccount && targetUserClient
			? getWorstCaseTokenAmounts(
					spotPosition,
					spotMarketAccount,
					strictOraclePrice,
					'Maintenance',
					maxMarginRatio
				)
			: { weight: null, weightedTokenValue: null };

	const initialAssetWeight =
		weightedTokenValueInitial !== null && weightedTokenValueInitial.gt(ZERO)
			? worstCaseWeightInitial
			: calculateAssetWeight(
					spotBalance?.balance?.val ?? ZERO,
					oraclePrice,
					spotMarketAccount,
					'Initial'
				);

	const maintenanceAssetWeight =
		weightedTokenValueMaintenance !== null &&
		weightedTokenValueMaintenance.lt(ZERO)
			? worstCaseWeightMaintenance
			: calculateAssetWeight(
					spotBalance?.balance?.val ?? ZERO,
					oraclePrice,
					spotMarketAccount,
					'Maintenance'
				);

	const scaleInitialAssetWeightStartBigNum = BigNum.from(
		spotMarketAccount?.scaleInitialAssetWeightStart || ZERO,
		QUOTE_PRECISION_EXP
	);
	const totalDepositsQuote =
		borrowLendData?.totalDepositsQuote ?? BigNum.zero();

	const isLoweredAssetWeight =
		totalDepositsQuote.gt(scaleInitialAssetWeightStartBigNum) &&
		!scaleInitialAssetWeightStartBigNum.eqZero();

	const handleDepositHistory = () => {
		safePush('/portfolio/history?table=deposits');
	};

	const handleShowDepositModal = () => {
		show({
			type: 'deposit',
			props: {
				collateralIndex: spotMarketConfig.marketIndex,
			},
		});
	};

	const handleShowWithdrawModal = () => {
		show({
			type: 'withdraw',
			props: {
				collateralIndex: spotMarketConfig.marketIndex,
			},
		});
	};

	return (
		<>
			<DrawerBody className="thin-scroll">
				<Section sectionLabel="Overview">
					<DetailRow
						label="Earning APY"
						value={
							<ApyBreakdownTooltipV2
								apyChunks={apyData.deposit.chunks}
								showTooltip={apyData.deposit.showTooltip}
							>
								<div className="flex flex-row items-center gap-1 text-positive-green">
									<NumberDisplay
										value={apyData.deposit.totalApyBN}
										options={{
											style: 'percent',
											digits: {
												kind: 'decimals',
												decimals: 4,
												rounding: 'truncate',
											},
											small: { mode: 'subscript', order: 'after-digits' },
											trimTrailingZeros: true,
										}}
										className="text-positive-green"
										displayOverride={
											!isFinite(apyData.deposit.totalApy) ||
											apyData.deposit.totalApy > 1000
												? '> 1,000%'
												: undefined
										}
									/>
								</div>
							</ApyBreakdownTooltipV2>
						}
					/>
					<DetailRow
						label={'Global deposits'}
						value={
							<div className="flex flex-row items-center gap-1">
								<MarketIcon
									marketSymbol={spotMarketConfig.uiSymbols.baseAssetSymbol}
								/>{' '}
								<NumberDisplay
									options={legacyTrim(
										PRESETS.tradePrecisionLegacy,
										borrowLendData?.totalDepositsBase ?? ZERO_BIGNUM
									)}
									value={borrowLendData?.totalDepositsBase ?? ZERO_BIGNUM}
								/>
							</div>
						}
					/>
					<DetailRow
						label={'Max deposits'}
						value={
							<div className="flex flex-row items-center gap-1">
								<MarketIcon
									marketSymbol={spotMarketConfig.uiSymbols.baseAssetSymbol}
								/>{' '}
								<NumberDisplay
									options={legacyTrim(
										PRESETS.tradePrecisionLegacy,
										maxDeposits
									)}
									value={maxDeposits}
								/>
							</div>
						}
					/>
					<DetailRow
						label={
							<Tooltip
								disabled={!isLoweredAssetWeight}
								content={
									<div className="flex flex-col">
										<span>
											The initial asset weight has been lowered to limit the
											margin extended by this token collateral to
											{` ${formatText(scaleInitialAssetWeightStartBigNum, PRESETS.usd)}`}
											. This does not impact existing positions or the
											maintenance asset weight.
										</span>

										<Link
											href={`${VELOCITY_DOCS_URL}/getting-started/cross-collateral-deposits`}
											openInNewTab
										>
											Learn more
										</Link>
									</div>
								}
							>
								Initial asset weight
							</Tooltip>
						}
						value={
							<div className="flex flex-row items-center gap-1">
								<NumberDisplay
									value={BigNum.from(
										initialAssetWeight
											.mul(new BN(100))
											.div(SPOT_MARKET_WEIGHT_PRECISION)
									)}
									options={PRESETS.percentTruncateTrim}
								/>
							</div>
						}
					/>
					<DetailRow
						label="Maintenance asset weight"
						value={
							<NumberDisplay
								value={BigNum.from(
									maintenanceAssetWeight
										.mul(new BN(100))
										.div(SPOT_MARKET_WEIGHT_PRECISION)
								)}
								options={PRESETS.percentTruncateTrim}
							/>
						}
					/>
				</Section>

				<Divider />

				<Section
					sectionLabel="Your Deposits"
					topRightNode={
						<TextButton
							onClick={handleDepositHistory}
							variant="secondary"
							rightIcon={ArrowRight}
						>
							History
						</TextButton>
					}
				>
					<DetailRow
						label="Your deposits"
						value={
							<NumberDisplay
								options={{
									...PRESETS.tradePrecisionTrim,
									suffix: spotMarketConfig.uiSymbols.baseAssetSymbol,
								}}
								value={userDeposits}
							/>
						}
					/>
					<DetailRow
						label={
							<DefinitionTooltip definitionId="balancesLiquidationPrice">
								Liquidation price
							</DefinitionTooltip>
						}
						value={
							liqPrice.gtZero() ? (
								<NumberDisplay
									options={legacyTrim(
										PRESETS.usdTradePrecisionLegacy,
										liqPrice
									)}
									value={liqPrice}
								/>
							) : (
								PRICE_FALLBACK_DISPLAY_VALUE
							)
						}
					/>
				</Section>

				<Divider />

				<Section sectionLabel="Deposit Rate History">
					<RateHistoryChart
						marketIndex={spotMarketConfig.marketIndex}
						type={'deposit'}
						showAverage
					/>
				</Section>

				<Divider />

				<Section sectionLabel="Interest Rate and Utilization">
					<BorrowLendRateToUtilizationChart
						marketIndex={spotMarketConfig.marketIndex}
						className="h-[200px]"
					/>
				</Section>

				<Divider />

				<Section sectionLabel="Initial Asset Weight">
					<InitialAssetWeightGraph
						marketIndex={spotMarketConfig.marketIndex}
						userPositionSize={userDeposits}
					/>
				</Section>
			</DrawerBody>
			<DrawerFooter className="flex w-full items-center justify-between gap-2">
				<Button
					size="lg"
					variant="primary"
					className="w-full"
					onClick={handleShowDepositModal}
				>
					Deposit
				</Button>

				{userDeposits.gtZero() && (
					<Button
						size="lg"
						className="w-full"
						variant="secondary"
						onClick={handleShowWithdrawModal}
					>
						Withdraw
					</Button>
				)}
			</DrawerFooter>
		</>
	);
};

const BorrowMarketContent = ({
	market: spotMarketConfig,
	targetAccountKey,
}: {
	market: UIMarket;
	targetAccountKey?: string;
}) => {
	const { show } = useModal();
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const borrowLendData = useVelocityStore((s) =>
		s.borrowLendData.find(
			(data) => data.bankIndex === spotMarketConfig.marketIndex
		)
	);
	const targetUserClient = useVelocityAccountStore((s) =>
		targetAccountKey
			? s.accounts[targetAccountKey]?.client
			: s.getCurrentUserAccount()?.client
	);
	const spotBalance = useVelocityAccountStore(
		(s) =>
			s.accounts[
				targetAccountKey ? targetAccountKey : (s.currentUserKey ?? '')
			]?.spotBalances?.find(
				(balance: { asset: { marketIndex: number } }) =>
					balance.asset.marketIndex === spotMarketConfig.marketIndex
			) || null
	);

	const spotMarketAccount = velocityClientIsReady
		? velocityClient?.getSpotMarketAccount(spotMarketConfig.marketIndex)
		: undefined;

	const spotPosition = useMemo(() => {
		return targetUserClient
			?.getActiveSpotPositions()
			.find((pos) => pos.marketIndex === spotMarketConfig.marketIndex);
	}, [targetUserClient, spotMarketConfig.marketIndex]);

	// Get unified APY data from the centralized hook
	const apyData = useApyBreakdownTooltipData(spotMarketConfig.marketIndex);

	// Extract commonly used values for readability
	const isPositiveTotalBorrowApy = apyData.borrowing.totalApy >= 0;

	const globalBorrows = borrowLendData?.totalBorrowsBase || ZERO_BIGNUM;
	const maxBorrows = borrowLendData?.borrowLimitBase || ZERO_BIGNUM;
	const borrowUtilization =
		borrowLendData?.borrowUtilization?.mul(ONE_HUNDRED) || ZERO_BIGNUM;

	const oraclePriceData = spotMarketAccount
		? velocityClient?.getOracleDataForSpotMarket(spotMarketAccount.marketIndex)
		: undefined;

	const strictOraclePrice = new StrictOraclePrice(
		oraclePriceData?.price ?? ZERO
	);
	const maxMarginRatio =
		targetUserClient?.getUserAccount()?.maxMarginRatio ?? 0;

	const {
		weight: worstCaseWeightInitial,
		weightedTokenValue: weightedTokenValueInitial,
	} =
		spotPosition && spotMarketAccount && targetUserClient
			? getWorstCaseTokenAmounts(
					spotPosition,
					spotMarketAccount,
					strictOraclePrice,
					'Initial',
					maxMarginRatio
				)
			: { weight: null, weightedTokenValue: null };

	const {
		weight: worstCaseWeightMaintenance,
		weightedTokenValue: weightedTokenValueMaintenance,
	} =
		spotPosition && spotMarketAccount && targetUserClient
			? getWorstCaseTokenAmounts(
					spotPosition,
					spotMarketAccount,
					strictOraclePrice,
					'Maintenance',
					maxMarginRatio
				)
			: { weight: null, weightedTokenValue: null };

	if (!spotMarketAccount) {
		return null;
	}

	const scaledInitialLiabilityWeight = calculateLiabilityWeight(
		spotBalance?.balance?.val ?? ZERO,
		spotMarketAccount,
		'Initial'
	);

	const scaledMaintenanceLiabilityWeight = calculateLiabilityWeight(
		spotBalance?.balance?.val ?? ZERO,
		spotMarketAccount,
		'Maintenance'
	);

	const userLiabilityWeight =
		weightedTokenValueInitial !== null && weightedTokenValueInitial.lt(ZERO)
			? worstCaseWeightInitial
			: scaledInitialLiabilityWeight;

	const userMaintenanceLiabilityWeight =
		weightedTokenValueMaintenance !== null &&
		weightedTokenValueMaintenance.lt(ZERO)
			? worstCaseWeightMaintenance
			: scaledMaintenanceLiabilityWeight;

	const displayLiquidationFee = `${
		((spotMarketAccount?.liquidatorFee ?? 0) +
			(spotMarketAccount?.ifLiquidationFee ?? 0)) /
		10000
	}%`;

	const userBorrows = ENUM_UTILS.match(
		spotBalance?.balanceType,
		SpotBalanceType.BORROW
	)
		? (spotBalance?.balance ?? ZERO_BIGNUM)
		: ZERO_BIGNUM;

	const liqPrice = BigNum.from(
		targetUserClient?.spotLiquidationPrice(spotMarketConfig.marketIndex),
		PRICE_PRECISION_EXP
	);

	const handleShowBorrowModal = () => {
		show({
			type: 'borrow',
			props: {
				collateralIndex: spotMarketConfig.marketIndex,
			},
		});
	};

	return (
		<>
			<DrawerBody className="thin-scroll">
				<Section sectionLabel="Overview">
					<DetailRow
						label="Borrow APY"
						value={
							<ApyBreakdownTooltipV2
								apyChunks={apyData.borrowing.chunks}
								showTooltip={apyData.borrowing.showTooltip}
							>
								<div className="flex flex-row items-center gap-1">
									<NumberDisplay
										className={
											isPositiveTotalBorrowApy
												? 'text-star-yellow'
												: 'text-positive-green'
										}
										value={apyData.borrowing.totalApyBN.abs()}
										options={{
											style: 'percent',
											digits: {
												kind: 'decimals',
												decimals: 4,
												rounding: 'truncate',
											},
											small: { mode: 'subscript', order: 'after-digits' },
											trimTrailingZeros: true,
										}}
									/>
								</div>
							</ApyBreakdownTooltipV2>
						}
					/>
					<DetailRow
						label={'Global borrows'}
						value={
							<div className="flex flex-row items-center gap-1">
								<MarketIcon
									marketSymbol={spotMarketConfig.uiSymbols.baseAssetSymbol}
								/>{' '}
								<NumberDisplay
									options={legacyTrim(
										PRESETS.tradePrecisionLegacy,
										globalBorrows
									)}
									value={globalBorrows}
								/>
							</div>
						}
					/>
					<DetailRow
						label={'Borrow availability'}
						value={
							<div className="flex flex-row items-center gap-1">
								<MarketIcon
									marketSymbol={spotMarketConfig.uiSymbols.baseAssetSymbol}
								/>{' '}
								<NumberDisplay
									options={legacyTrim(
										PRESETS.tradePrecisionLegacy,
										maxBorrows.gtZero() ? maxBorrows : ZERO_BIGNUM
									)}
									value={maxBorrows.gtZero() ? maxBorrows : ZERO_BIGNUM}
								/>
							</div>
						}
					/>
					<DetailRow
						label={'Borrow utilization'}
						value={
							<div className="flex flex-row items-center gap-1">
								<NumberDisplay
									options={PRESETS.percentTruncate}
									value={borrowUtilization}
								/>
							</div>
						}
					/>
					<DetailRow
						label={'Initial liability weight'}
						value={
							<div className="flex flex-row items-center gap-1">
								<NumberDisplay
									options={PRESETS.percentTruncateTrim}
									value={BigNum.from(
										userLiabilityWeight
											.mul(new BN(100))
											.div(SPOT_MARKET_WEIGHT_PRECISION)
									)}
								/>
							</div>
						}
					/>
					<DetailRow
						label={'Maintenance liability weight'}
						value={
							<div className="flex flex-row items-center gap-1">
								<NumberDisplay
									options={PRESETS.percentTruncateTrim}
									value={BigNum.from(
										userMaintenanceLiabilityWeight
											.mul(new BN(100))
											.div(SPOT_MARKET_WEIGHT_PRECISION)
									)}
								/>
							</div>
						}
					/>
					<DetailRow
						label={'Liquidation fee'}
						value={
							<div className="flex flex-row items-center gap-1">
								<NumberDisplay
									options={PRESETS.percentTruncate}
									value={BigNum.zero()}
									displayOverride={displayLiquidationFee}
								/>
							</div>
						}
					/>
				</Section>

				<Divider />

				<Section sectionLabel="Your Borrows">
					<DetailRow
						label="Your borrows"
						value={
							<NumberDisplay
								options={{
									...PRESETS.tradePrecisionTrim,
									suffix: spotMarketConfig.uiSymbols.baseAssetSymbol,
								}}
								value={userBorrows}
							/>
						}
					/>
					<DetailRow
						label={
							<DefinitionTooltip definitionId="balancesLiquidationPrice">
								Liquidation price
							</DefinitionTooltip>
						}
						value={
							liqPrice.gtZero() ? (
								<NumberDisplay
									options={legacyTrim(
										PRESETS.usdTradePrecisionLegacy,
										liqPrice
									)}
									value={liqPrice}
								/>
							) : (
								PRICE_FALLBACK_DISPLAY_VALUE
							)
						}
					/>
				</Section>

				<Divider />

				<Section sectionLabel="Borrow Rate History">
					<RateHistoryChart
						marketIndex={spotMarketConfig.marketIndex}
						type={'borrow'}
						showAverage
					/>
				</Section>

				<Divider />

				<Section sectionLabel="Interest Rate and Utilization">
					<BorrowLendRateToUtilizationChart
						marketIndex={spotMarketConfig.marketIndex}
						className="h-[200px]"
					/>
				</Section>
			</DrawerBody>
			<DrawerFooter className="flex w-full items-center justify-between gap-2">
				<Button
					size="lg"
					variant="primary"
					className="w-full"
					onClick={handleShowBorrowModal}
				>
					Borrow
				</Button>
			</DrawerFooter>
		</>
	);
};

const BorrowLendDetailsDrawer = () => {
	const set = useDrawersStore((s) => s.set);
	const { market, displayTab, targetAccountKey } = useDrawersStore(
		(s) => s.borrowLendDetails
	);
	if (!market) {
		return null;
	}

	const uiMarket = UIMarket.createSpotMarket(market.marketIndex);

	const setSelectedTab = (value: BorrowLendTabValue) => {
		set((s) => {
			s.borrowLendDetails.displayTab = value;
		});
	};

	return (
		<DrawerContainer
			drawerName="borrowLendDetails"
			className="thin-scroll w-full overflow-y-auto sm:w-[400px] sm:max-w-[400px]"
		>
			<div className="flex flex-col gap-lg bg-container-bg">
				<DrawerTitle className="p-0">
					<div className="flex items-center gap-1">
						<MarketIcon
							sizeClass="size-4"
							marketSymbol={uiMarket.uiSymbols.baseAssetSymbol}
						/>
						<span className="ml-1 typo-label-md">
							{formatMarketSymbolByContext(market.symbol, 'borrow_lend')}
						</span>
					</div>
				</DrawerTitle>

				<div className="px-lg pb-xs">
					<Tabs
						variant="secondary"
						options={TabOptions}
						activeTab={displayTab}
						onTabChange={setSelectedTab}
					/>
				</div>
			</div>
			{displayTab === 'deposit' && (
				<DepositMarketContent
					market={uiMarket}
					targetAccountKey={targetAccountKey}
				/>
			)}
			{displayTab === 'borrow' && (
				<BorrowMarketContent
					market={uiMarket}
					targetAccountKey={targetAccountKey}
				/>
			)}
		</DrawerContainer>
	);
};

export default memo(BorrowLendDetailsDrawer);
